import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

const Stat = ({ label, value, hint }) => (
  <div className="bg-black bg-opacity-30 p-4 rounded-xl">
    <div className="text-xs text-purple-200">{label}</div>
    <div className="text-2xl font-bold">{value ?? "-"}</div>
    {hint && <div className="text-xs text-purple-100 mt-1 opacity-80">{hint}</div>}
  </div>
);

function arrToMap(arr, key = "_id") {
  const out = {};
  (arr || []).forEach((x) => {
    if (!x) return;
    out[x[key]] = Number(x.count || 0);
  });
  return out;
}

export default function AdminFeedbackPanel() {
  const [metrics, setMetrics] = useState(null); // optional (only if you have /api/admin/metrics)
  const [fbSummary, setFbSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState("");
  const [metricsErr, setMetricsErr] = useState("");

  const load = async () => {
    setErr("");
    setMetricsErr("");

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Always load feedback summary (this should exist now)
      const fbRes = await axios.get(`${API}/api/feedback/admin/summary`, { headers });
      setFbSummary(fbRes.data);
      setRecent(fbRes.data.recent || []);

      // Try to load metrics if you’ve built it; otherwise ignore gracefully
      try {
        const mRes = await axios.get(`${API}/api/admin/metrics`, { headers });
        setMetrics(mRes.data);
      } catch (e) {
        setMetrics(null);
        setMetricsErr(e?.response?.data?.error || "Metrics endpoint not set up yet.");
      }
    } catch (e) {
      console.error("Admin panel load error:", e);
      setErr(e?.response?.data?.error || "Failed to load admin panel");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ratingCounts = useMemo(() => {
    if (!fbSummary) return {};
    return arrToMap(fbSummary.breakdownByRating);
  }, [fbSummary]);

  const typeCounts = useMemo(() => {
    if (!fbSummary) return {};
    return arrToMap(fbSummary.breakdownByType);
  }, [fbSummary]);

  const overallResonance = useMemo(() => {
    const avg = fbSummary?.overallResonance?.avgScore;
    if (avg == null) return null;
    const pct = Math.round((Number(avg) / 2) * 100);
    return { avgScore: Number(avg), pct, count: fbSummary?.overallResonance?.count || 0 };
  }, [fbSummary]);

  const resonanceByType = useMemo(() => {
    return fbSummary?.resonanceByType || [];
  }, [fbSummary]);

  return (
    <div className="max-w-4xl mx-auto text-white">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Admin: Analytics & Feedback</h2>
        <button
          onClick={load}
          className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-xl"
        >
          Refresh
        </button>
      </div>

      {err && (
        <div className="mb-4 text-red-200 bg-red-900 bg-opacity-30 px-4 py-2 rounded-xl">
          {err}
        </div>
      )}

      {metricsErr && (
        <div className="mb-4 text-yellow-200 bg-yellow-900 bg-opacity-20 px-4 py-2 rounded-xl">
          {metricsErr}
        </div>
      )}

      {metrics && (
        <>
          <h3 className="text-lg font-semibold mb-2">Users</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Total users" value={metrics.users?.total} />
            <Stat label="New (24h)" value={metrics.users?.last24h} />
            <Stat label="New (7d)" value={metrics.users?.last7d} />
            <Stat label="New (30d)" value={metrics.users?.last30d} />
          </div>

          <h3 className="text-lg font-semibold mb-2">Products</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Core owners" value={metrics.products?.core || 0} />
            <Stat label="Guardian owners" value={metrics.products?.guardian || 0} />
            <Stat label="Blueprint owners" value={metrics.products?.blueprint || 0} />
            <Stat label="Subscription: Core" value={metrics.subscriptions?.core || 0} />
          </div>

          <h3 className="text-lg font-semibold mb-2">Cosmic Blueprints</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
            <Stat label="Total orders" value={metrics.blueprints?.total} />
            <Stat label="Gift orders" value={metrics.blueprints?.gifts} />
            <Stat label="Month type" value={metrics.blueprints?.byType?.month || 0} />
            <Stat label="Year type" value={metrics.blueprints?.byType?.year || 0} />
          </div>

          <div className="bg-black bg-opacity-20 p-4 rounded-xl mb-6">
            <div className="font-semibold mb-2">Blueprint status</div>
            <div className="text-sm text-purple-100 grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(metrics.blueprints?.byStatus || {}).map(([k, v]) => (
                <div key={k} className="bg-black bg-opacity-30 p-2 rounded">
                  <span className="text-purple-200">{k}</span>: <b>{v}</b>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!fbSummary ? (
        <div>Loading feedback analytics...</div>
      ) : (
        <>
          <h3 className="text-lg font-semibold mb-2">Feedback Analytics</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Total feedback" value={fbSummary.total} />
            <Stat
              label="Resonance score"
              value={overallResonance ? `${overallResonance.pct}%` : "-"}
              hint={
                overallResonance
                  ? `Avg=${Number(overallResonance.avgScore).toFixed(2)} / 2 (${overallResonance.count} votes)`
                  : ""
              }
            />
            <Stat label="Very much" value={ratingCounts.very || 0} />
            <Stat label="Not really" value={ratingCounts.not || 0} />
          </div>

          <div className="bg-black bg-opacity-20 p-4 rounded-xl mb-6">
            <div className="font-semibold mb-2">Counts by type</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div className="bg-black bg-opacity-30 p-3 rounded-xl">
                <div className="text-purple-200 font-semibold">personal-daily</div>
                <div>{typeCounts["personal-daily"] || 0}</div>
              </div>
              <div className="bg-black bg-opacity-30 p-3 rounded-xl">
                <div className="text-purple-200 font-semibold">personal-weekly</div>
                <div>{typeCounts["personal-weekly"] || 0}</div>
              </div>
              <div className="bg-black bg-opacity-30 p-3 rounded-xl">
                <div className="text-purple-200 font-semibold">sign-daily</div>
                <div>{typeCounts["sign-daily"] || 0}</div>
              </div>
            </div>
          </div>

          <div className="bg-black bg-opacity-20 p-4 rounded-xl mb-6">
            <div className="font-semibold mb-2">Resonance by type</div>
            {resonanceByType.length === 0 ? (
              <div className="text-gray-300">No resonance data yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                {resonanceByType.map((r) => {
                  const pct = Math.round((Number(r.avgScore) / 2) * 100);
                  return (
                    <div key={r._id} className="bg-black bg-opacity-30 p-3 rounded-xl">
                      <div className="text-purple-200 font-semibold">{r._id}</div>
                      <div>
                        <b>{pct}%</b> resonance <span className="opacity-80">({r.count} votes)</span>
                      </div>
                      <div className="opacity-80">Avg {Number(r.avgScore).toFixed(2)} / 2</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-black bg-opacity-30 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-3">Latest feedback</h3>
            {recent.length === 0 ? (
              <div className="text-gray-300">No feedback yet.</div>
            ) : (
              <div className="space-y-3">
                {recent.map((f) => (
                  <div key={f._id} className="bg-black bg-opacity-20 p-3 rounded-xl">
                    <div className="text-xs text-purple-200">
                      <b>{f.type}</b> • rating: <b>{f.rating}</b> •{" "}
                      {new Date(f.createdAt).toLocaleString()}
                      {f.user?.email ? (
                        <>
                          {" "}
                          • user: <b>{f.user.fullName || f.user.email}</b>
                        </>
                      ) : null}
                    </div>
                    {f.comment ? (
                      <div className="mt-2 text-sm whitespace-pre-line">{f.comment}</div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-400">(No comment)</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
